import React, { ContextType, Fragment } from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { IFormatter } from '@kurtosys/types/data/IFormatter.js';
import { action, computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import { IDatePickerProps } from '../DatePicker/models/index.js';
import { IDropdownItem } from '../Dropdown/models/IDropdownItem.js';
import { IDropdownProps } from '../Dropdown/models/index.js';
import { ITranslationContextValue } from '../Translate/models/index.js';
import { TranslationContext } from '../Translate/Translate.js';

import { IDateSelectionConfiguration, IDateSelectionProps, IDateSelectionState } from './models/index.js';
import CalendarDateLabel from './styledComponents/CalendarDateLabel.js';
import CalendarDateWrapper from './styledComponents/CalendarDateWrapper.js';
import CalendarEndDate from './styledComponents/CalendarEndDate.js';
import CalendarStartDate from './styledComponents/CalendarStartDate.js';
import EndDate from './styledComponents/EndDate.js';
import StartDate from './styledComponents/StartDate.js';
import Wrapper from './styledComponents/Wrapper.js';
import { DATE_SELECTION_CATEGORY_KEY, DATE_SELECTION_COMPONENT_KEY } from './DateSelection.meta.js';

export class DateSelection extends ComponentBase<
	IDateSelectionProps,
	IDateSelectionState,
	IDateSelectionConfiguration
> {
	categoryKey = DATE_SELECTION_CATEGORY_KEY;
	componentKey = DATE_SELECTION_COMPONENT_KEY;

	items: IDropdownItem[] = [];
	static contextType = TranslationContext;
	translationContext!: ContextType<typeof TranslationContext>;
	calendarStartDateLabelId = `date_selection_label_${common.commonUtils.generateGuid()}`;
	calendarEndDateLabelId = `date_selection_label_${common.commonUtils.generateGuid()}`;

	constructor(props: IDateSelectionProps) {
		super(props);

		makeObservable(this, {
			initialize: action,
			initialiseDropdownItems: action,
			startDateProps: computed,
			endDateProps: computed,
			allowedValues: computed,
			calendarStartDateProps: computed,
			calendarEndDateProps: computed,
			calendarStartLabel: computed,
			calendarEndLabel: computed,
			selectedDropdownStartDate: computed,
			selectedDropdownEndDate: computed,
			selectedCalendarStartDate: computed,
			selectedCalendarStartDateAsDate: computed,
			selectedCalendarEndDate: computed,
			selectedCalendarEndDateAsDate: computed,
		});

		this.initialize();
	}

	initialize = () => {
		const { mode = 'dropdown' } = this.props;
		if (mode === 'dropdown') {
			this.initialiseDropdownItems();
		}
	};

	componentDidUpdate(previousProps: IDateSelectionProps) {
		if (this.dateRangesDiffer(previousProps.dateRange, this.props.dateRange)) {
			this.initialize();
		}
	}

	dateRangesDiffer = (oldRange?: string[], newRange?: string[]) => {
		return !(
			oldRange === newRange ||
			(oldRange &&
				newRange &&
				common.commonUtils.stringifySafe(oldRange) === common.commonUtils.stringifySafe(newRange))
		);
	};

	initialiseDropdownItems() {
		const { dateRange = [] } = this.props;
		this.items = dateRange.map((date) => this.getDateAsDropdownItem(date));
	}

	get format() {
		const { formatOverride, dateFormat = 'date' } = this.props;
		const globalFormat =
			this.globalConfiguration &&
			this.globalConfiguration.formats &&
			this.globalConfiguration.formats[dateFormat];
		const format = formatOverride || globalFormat;
		if (format) {
			if (!format.locale) {
				format.locale = this.translationContext && this.translationContext.culture;
			}
			if (!format.options) {
				format.options = {};
			}
		}
		return format as IFormatter;
	}

	getDateAsDropdownItem = (date: string): IDropdownItem => {
		return {
			value: date,
			label: this.format ? (common.commonUtils.formatter(this.format, date) as string) : date,
		};
	};

	get startDateProps(): IDropdownProps {
		const { onChange, startDateProps } = this.props;
		const selectedItem = !common.commonUtils.isNullOrEmpty(this.selectedDropdownStartDate)
			? this.getDateAsDropdownItem(this.selectedDropdownStartDate)
			: undefined;
		const onChangeEvent = (item: IDropdownItem | IDropdownItem[]) => {
			if (startDateProps) {
				startDateProps.value = (item as IDropdownItem).value;
				if (onChange) {
					onChange((item as IDropdownItem).value, this.selectedDropdownEndDate);
				}
			}
		};
		return {
			selectedItem,
			items: common.commonUtils.isNullOrEmpty(this.selectedDropdownEndDate)
				? this.items
				: this.items.filter((item) => item.value <= this.selectedDropdownEndDate),
			placeholder: (startDateProps && startDateProps.placeholder) || 'Start Date',
			label: startDateProps && startDateProps.label,
			onChange: onChangeEvent,
		};
	}

	get endDateProps(): IDropdownProps {
		const { onChange, endDateProps } = this.props;
		const selectedItem = !common.commonUtils.isNullOrEmpty(this.selectedDropdownEndDate)
			? this.getDateAsDropdownItem(this.selectedDropdownEndDate)
			: undefined;
		const onChangeEvent = (item: IDropdownItem | IDropdownItem[]) => {
			if (endDateProps) {
				endDateProps.value = (item as IDropdownItem).value;
				if (onChange) {
					onChange(this.selectedDropdownStartDate, (item as IDropdownItem).value);
				}
			}
		};
		return {
			selectedItem,
			items: common.commonUtils.isNullOrEmpty(this.selectedDropdownStartDate)
				? this.items
				: this.items.filter((item) => item.value >= this.selectedDropdownStartDate),
			placeholder: (endDateProps && endDateProps.placeholder) || 'End Date',
			label: endDateProps && endDateProps.label,
			onChange: onChangeEvent,
		};
	}

	get allowedValues(): string[] {
		const { dateRange = [] } = this.props;
		return dateRange;
	}

	get calendarStartDateProps(): IDatePickerProps {
		const { onChange, calendarStartDateProps } = this.props;
		const onChangeEvent = (value: string) => {
			if (calendarStartDateProps) {
				calendarStartDateProps.value = value;
				if (onChange) {
					onChange(value, this.selectedCalendarEndDate);
				}
			}
		};

		return {
			...calendarStartDateProps,
			value: this.selectedCalendarStartDate,
			labelId: this.calendarStartDateLabelId,
			isDateDisabled: this.isStartDateDisabled,
			onChange: onChangeEvent,
		} as IDatePickerProps;
	}

	get calendarEndDateProps(): IDatePickerProps {
		const { onChange, calendarEndDateProps } = this.props;
		const onChangeEvent = (value: string) => {
			if (calendarEndDateProps) {
				calendarEndDateProps.value = value;
				if (onChange) {
					onChange(this.selectedCalendarStartDate, value);
				}
			}
		};

		return {
			...calendarEndDateProps,
			value: this.selectedCalendarEndDate,
			labelId: this.calendarEndDateLabelId,
			isDateDisabled: this.isEndDateDisabled,
			onChange: onChangeEvent,
		} as IDatePickerProps;
	}

	isDateAllowed = (date: Date) => {
		const isInAllowed = this.allowedValues.find((value: string) => {
			const allowedDate = common.commonUtils.printISODateFromString(value);
			const currentDate = common.commonUtils.printISODate(date);
			return allowedDate === currentDate;
		});
		return isInAllowed !== undefined;
	};

	isStartDateDisabled = (date: Date) => {
		const isDateAllowed = this.isDateAllowed(date);
		const hasEndDate = !common.commonUtils.isNullOrEmpty(this.selectedCalendarEndDate);
		const isAfterEndDate = !hasEndDate || (hasEndDate && date > this.selectedCalendarEndDateAsDate);
		return !isDateAllowed || isAfterEndDate;
	};

	isEndDateDisabled = (date: Date) => {
		const isDateAllowed = this.isDateAllowed(date);
		const hasStartDate = !common.commonUtils.isNullOrEmpty(this.selectedCalendarStartDate);
		const isBeforeStartDate = !hasStartDate || (hasStartDate && date < this.selectedCalendarStartDateAsDate);
		return !isDateAllowed || isBeforeStartDate;
	};

	displayDate(selectedDate: string, translationContext: ITranslationContextValue) {
		const { culture, translate } = translationContext;
		return this.format
			? (common.commonUtils.formatter(this.format, selectedDate, translate) as string)
			: common.commonUtils.formatDateTime(culture, {}, selectedDate);
	}

	get calendarStartLabel() {
		const { calendarStartDateProps: calendarStart } = this.props;
		return (calendarStart && calendarStart.label) || 'Start Date';
	}

	get calendarEndLabel() {
		const { calendarEndDateProps: calendarEnd } = this.props;
		return (calendarEnd && calendarEnd.label) || 'End Date';
	}

	get selectedDropdownStartDate() {
		const { startDateProps, dateRange = [] } = this.props;
		const startDate = startDateProps && startDateProps.value;
		if (startDate && dateRange.includes(startDate)) {
			return startDate;
		}
		return '';
	}

	get selectedDropdownEndDate() {
		const { startDateProps, endDateProps, dateRange = [] } = this.props;
		const startDate = startDateProps && startDateProps.value;
		const endDate = endDateProps && endDateProps.value;

		if (endDate && dateRange.includes(endDate)) {
			if (!startDate || startDate <= endDate) {
				return endDate;
			}
		}
		return '';
	}

	get selectedCalendarStartDate() {
		const { calendarStartDateProps } = this.props;
		const startDate = calendarStartDateProps && calendarStartDateProps.value;
		if (startDate && this.allowedValues.includes(startDate)) {
			const date = common.commonUtils.printISODateFromString(startDate);
			return date;
		}
		return '';
	}

	get selectedCalendarStartDateAsDate() {
		return common.commonUtils.parseISODate(this.selectedCalendarStartDate) || new Date();
	}

	get selectedCalendarEndDate() {
		const { calendarStartDateProps, calendarEndDateProps } = this.props;
		const startDate = calendarStartDateProps && calendarStartDateProps.value;
		const endDate = calendarEndDateProps && calendarEndDateProps.value;
		if (endDate && this.allowedValues.includes(endDate)) {
			if (!startDate || startDate <= endDate) {
				return common.commonUtils.printISODateFromString(endDate);
			}
		}
		return '';
	}

	get selectedCalendarEndDateAsDate() {
		return common.commonUtils.parseISODate(this.selectedCalendarEndDate) || new Date();
	}

	render() {
		const { className, mode = 'dropdown' } = this.props;

		return (
			<Wrapper className={className}>
				<TranslationContext.Consumer>
					{(translationContext: ITranslationContextValue) => {
						this.translationContext = translationContext;
						return (
							<React.Fragment>
								{mode === 'dropdown' && (
									<Fragment>
										<StartDate {...this.startDateProps} />
										<EndDate {...this.endDateProps} />
									</Fragment>
								)}
								{mode === 'calendar' && (
									<Fragment>
										<CalendarDateWrapper>
											<CalendarDateLabel id={this.calendarStartDateLabelId}>
												{this.calendarStartLabel}
											</CalendarDateLabel>
											<CalendarStartDate {...this.calendarStartDateProps} />
										</CalendarDateWrapper>
										<CalendarDateWrapper>
											<CalendarDateLabel id={this.calendarEndDateLabelId}>
												{this.calendarEndLabel}
											</CalendarDateLabel>
											<CalendarEndDate {...this.calendarEndDateProps} />
										</CalendarDateWrapper>
									</Fragment>
								)}
							</React.Fragment>
						);
					}}
				</TranslationContext.Consumer>
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(
	observer(DateSelection),
	DATE_SELECTION_CATEGORY_KEY,
	DATE_SELECTION_COMPONENT_KEY,
);
